import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Coins, Grape } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";

const BEST_VALUE_PACKAGE = "pack75";

export default function CreditsTab() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [balance, setBalance] = useState(null);
  const [packages, setPackages] = useState(null);
  const [promo, setPromo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [buyingId, setBuyingId] = useState(null);
  const [polling, setPolling] = useState(false);

  const loadData = useCallback(() => {
    api.get("/payments/credits/balance").then(({ data }) => setBalance(data.balance));
    api.get("/payments/packages").then(({ data }) => { setPackages(data.credit_packages); setPromo(data.promo); });
    api.get("/payments/credits/transactions").then(({ data }) => setTransactions(data));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(t("credits.paymentSuccess"));
          setPolling(false);
          searchParams.delete("session_id");
          setSearchParams(searchParams);
          loadData();
          return;
        }
        if (data.status === "expired") {
          toast.error(t("credits.paymentFailed"));
          setPolling(false);
          return;
        }
      } catch (e) { /* ignore, retry */ }
      if (attempts < 8) setTimeout(poll, 2500);
      else setPolling(false);
    };
    poll();
  }, [searchParams]);

  const handleBuy = async (packageId) => {
    setBuyingId(packageId);
    try {
      const { data } = await api.post("/payments/checkout/credits", { package_id: packageId, origin_url: window.location.origin.concat("/dashboard/producer") });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
      setBuyingId(null);
    }
  };

  if (!packages || balance === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7]" /></div>;

  return (
    <div data-testid="producer-credits-tab">
      {polling && (
        <div className="mb-6 rounded-xl bg-[#4ba3b7]/10 border border-[#4ba3b7]/20 p-4 text-sm flex items-center gap-2" data-testid="credits-payment-polling-banner">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("credits.paymentPending")}
        </div>
      )}
      {promo?.active && (
        <div className="mb-6 rounded-2xl bg-[#2B2B2B] text-white p-5 flex items-center gap-3" data-testid="credits-promo-banner">
          <Grape className="h-6 w-6 text-[#dde165] shrink-0" />
          <div>
            <p className="font-heading font-bold">{t("credits.promoBannerTitle")}</p>
            <p className="text-sm text-white/70">{t("credits.promoBannerText", { percent: promo.bonus_percent })}</p>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6 mb-8 flex items-center gap-4">
        <Coins className="h-8 w-8 text-[#4ba3b7]" />
        <div>
          <p className="text-xs text-[#2B2B2B]/50 uppercase tracking-wide font-bold">{t("credits.creditBalance")}</p>
          <p className="font-heading text-2xl font-bold" data-testid="producer-credit-balance">{balance} {t("common.credits")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {Object.entries(packages).map(([key, pkg]) => (
          <div key={key} className={`relative bg-white rounded-2xl border shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6 text-center ${key === BEST_VALUE_PACKAGE ? "border-[#4ba3b7] ring-1 ring-[#4ba3b7]/30" : "border-black/5"}`} data-testid={`credit-package-${key}`}>
            {key === BEST_VALUE_PACKAGE && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4ba3b7] text-white border-0 rounded-full text-xs">
                {t("credits.bestValue")}
              </Badge>
            )}
            <p className="text-3xl font-heading font-bold text-[#4ba3b7] mb-1" data-testid={`credit-package-${key}-total`}>{pkg.total_credits}</p>
            {pkg.bonus_credits > 0 ? (
              <p className="text-xs text-[#34C759] font-semibold mb-4">{t("credits.bonusIncluded", { base: pkg.credits, bonus: pkg.bonus_credits })}</p>
            ) : (
              <p className="text-xs text-[#2B2B2B]/50 mb-4">{t("common.credits")}</p>
            )}
            <p className="text-lg font-bold mb-1">{pkg.price}&euro;</p>
            <p className="text-xs text-[#2B2B2B]/50 mb-4">{(pkg.price / pkg.total_credits).toFixed(2)}&euro;{t("credits.perRecruitmentSuffix")}</p>
            <Button onClick={() => handleBuy(key)} disabled={buyingId === key} className="w-full rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A]" data-testid={`buy-credit-package-${key}-button`}>
              {buyingId === key ? t("common.loading") : t("credits.buyThisPack")}
            </Button>
          </div>
        ))}
      </div>

      <h3 className="font-heading font-bold mb-4">{t("credits.transactionHistory")}</h3>
      <div className="space-y-2">
        {transactions.map((tx) => (
          <div key={tx.id} className="flex justify-between text-sm bg-white rounded-xl border border-black/5 p-3" data-testid={`credit-transaction-${tx.id}`}>
            <span>{tx.type === "signup_bonus" ? t("credits.signupBonus") : tx.type === "purchase" ? t("credits.buyCredits") : t("application.unlockCandidate")}</span>
            <span className={tx.amount > 0 ? "text-[#34C759] font-semibold" : "text-[#2B2B2B]/60"}>{tx.amount > 0 ? "+" : ""}{tx.amount}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
